import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DataList, PageHeader, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { Button, ConfirmDialog, Section, formatRelative, useAuth, useToast } from "@ozpanel/ui";

// The cross-account API token queue — beside Port and Redis requests, the same shape. An account asks for a
// token with exactly what it needs; approving stores no secret: the user who asked reveals it once from
// their own console or API (internal/auth/tokenrequests.go). Refusing needs a reason the account sees.

interface TokenRequest {
  id: number;
  tenant_id: number;
  tenant?: string;
  requested_email?: string;
  name: string;
  scopes: string[];
  allow_dangerous: boolean;
  allowed_ips: string[];
  ttl_days: number;
  purpose: string;
  status: "requested" | "approved" | "denied" | "issued";
  decided_reason?: string;
  requested_at: string;
  issued_at?: string;
}

export function TokenRequestsScreen() {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const q = useQuery({
    queryKey: ["token-requests"],
    queryFn: () => client.apiFetch<{ items: TokenRequest[]; pending: number }>(`/tokens/requests`),
    refetchInterval: 15000,
  });
  const [deciding, setDeciding] = React.useState<{ req: TokenRequest; approve: boolean } | null>(null);
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const decide = async () => {
    if (!deciding) return;
    setBusy(true);
    try {
      await client.apiFetch(`/tokens/requests/${deciding.req.id}/${deciding.approve ? "approve" : "deny"}`, {
        method: "POST",
        body: JSON.stringify({ reason: reason.trim() }),
      });
      notify({ kind: "success", title: `${deciding.req.name} ${deciding.approve ? "approved — the requester reveals it once" : "refused"}` });
      setDeciding(null);
      setReason("");
      void qc.invalidateQueries({ queryKey: ["token-requests"] });
    } catch (e) {
      notify({ kind: "error", title: "The decision failed", body: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  const cols: DataListColumn<TokenRequest>[] = [
    { key: "tenant", label: "Account", render: (x) => `${x.tenant || "—"} (${x.tenant_id})` },
    { key: "name", label: "Token", render: (x) => <span className="font-mono text-[12.5px]">{x.name}</span> },
    {
      key: "scopes",
      label: "Allows",
      render: (x) => (
        <span className="font-mono text-[11.5px]">
          {x.scopes.join(" ")} {x.allow_dangerous && <StatusPill tone="warn">dangerous</StatusPill>}
        </span>
      ),
    },
    { key: "allowed_ips", label: "From", render: (x) => <span className="font-mono text-[11.5px]">{x.allowed_ips.length ? x.allowed_ips.join(" ") : "anywhere"}</span> },
    { key: "ttl_days", label: "Lives", render: (x) => `${x.ttl_days} d` },
    { key: "purpose", label: "For", render: (x) => `${x.purpose}${x.requested_email ? ` — ${x.requested_email}` : ""}` },
    {
      key: "status",
      label: "Status",
      render: (x) => (
        <span className="inline-flex flex-col">
          <StatusPill tone={x.status === "approved" || x.status === "issued" ? "ok" : x.status === "denied" ? "bad" : "warn"}>
            {x.status === "issued" ? "revealed" : x.status}
          </StatusPill>
          {x.decided_reason && <span className="mt-1 text-[11.5px] text-muted">{x.decided_reason}</span>}
        </span>
      ),
    },
    { key: "requested_at", label: "Asked", render: (x) => formatRelative(x.requested_at) },
  ];

  const r = deciding?.req;
  return (
    <div>
      <PageHeader title="Token requests" subtitle="Accounts asking for an API token — approve to let the requester reveal it once" />
      <Section title="Requests" hint={q.data ? `${q.data.pending} waiting` : undefined} bodyClassName="p-0">
        <div className="p-4">
          <DataList
            items={q.data?.items ?? []}
            columns={cols}
            getRowKey={(x) => String(x.id)}
            loading={q.isLoading}
            error={q.error}
            errorContext="the API token requests"
            emptyMessage="No requests."
            rowActions={(x) =>
              x.status === "requested" ? (
                <div className="flex gap-2 text-[12px]">
                  <Button size="sm" variant="ghost" onClick={() => setDeciding({ req: x, approve: true })}>Approve</Button>
                  <Button size="sm" variant="danger" onClick={() => setDeciding({ req: x, approve: false })}>Refuse</Button>
                </div>
              ) : null
            }
          />
        </div>
      </Section>
      <ConfirmDialog
        open={!!deciding}
        onOpenChange={(o) => !o && setDeciding(null)}
        title={r ? `${deciding?.approve ? "Approve" : "Refuse"} ${r.name}` : ""}
        confirmLabel={deciding?.approve ? "Approve" : "Refuse"}
        confirmDisabled={!deciding?.approve && reason.trim() === ""}
        pending={busy}
        onConfirm={() => void decide()}
        body={
          <div className="space-y-2 text-[12.5px]">
            {r && (
              <>
                <div>
                  {r.tenant} ({r.tenant_id}){r.requested_email ? `, ${r.requested_email}` : ""} asks for <b className="font-mono">{r.name}</b> — {r.purpose}
                </div>
                <div className="font-mono text-[11.5px]">scopes: {r.scopes.join(" ")}</div>
                <div className="font-mono text-[11.5px]">from: {r.allowed_ips.length ? r.allowed_ips.join(" ") : "ANY address"}</div>
                <div className="text-[11.5px]">
                  lives {r.ttl_days} days from approval{r.allow_dangerous ? " · DANGEROUS operations allowed" : ""}
                </div>
              </>
            )}
            <label className="block">
              Reason {deciding?.approve ? "(optional)" : "(the account sees it)"}
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full rounded border border-line2 bg-bg px-3 py-2 text-[13px] outline-none focus:border-accent"
              />
            </label>
            <div className="text-[11.5px] text-muted">Nothing is minted on approval. The requester reveals the token once; it never passes through you.</div>
          </div>
        }
      />
    </div>
  );
}
